<script setup lang="ts">
import '@banno/jha-wc/src/progress/jha-progress/jha-progress.js';
import '@banno/jha-wc/src/forms/jha-form-checkbox/jha-form-checkbox.js';
import '@banno/jha-wc/src/cards/jha-card/jha-card.js';
import '@banno/jha-wc/src/jha-flex-wrapper/jha-flex-wrapper.js';
import '@banno/jha-wc/src/buttons/jha-button/jha-button.js';
import {onMounted, onUnmounted, ref} from "vue";

const URL = ref<string | null>(null)
const configLoaded = ref(false)
const configError = ref(false)
const localStorageKey = 'oa-domain-redirect--do-not-remind'

interface RedirectConfig {
  oaDomainRedirectTarget: string
}

const loadRedirectConfig = () =>
    fetch('/redirect-config.json')
        .then(response => {
          return response.json() as Promise<RedirectConfig>
        })
        .then(response => {
          URL.value = response.oaDomainRedirectTarget
        })
        .catch(() => {
          configError.value = true
        })
        .finally(() => {
          configLoaded.value = true
        })

const COUNTDOWN_MS = 5000
const msRemaining = ref(COUNTDOWN_MS)
const isCounting = ref(false)
const checked = ref(false)
let intervalId: ReturnType<typeof setInterval> | undefined

const redirect = () => {
  if (!URL.value) return
  clearInterval(intervalId)
  location.replace(URL.value)
}

const toggleCheckbox = (event: CustomEvent<{ checked: boolean }>) => {
  localStorage.setItem(localStorageKey, String(event.detail.checked))
  checked.value = event.detail.checked

  if (!checked.value) {
    clearInterval(intervalId)
    isCounting.value = false
  }
}

const startCountdown = (() => {
  const startedAt = Date.now()
  msRemaining.value = COUNTDOWN_MS
  isCounting.value = true

  intervalId = setInterval(() => {
    const remaining = COUNTDOWN_MS - (Date.now() - startedAt)

    if (remaining <= 0) {
      redirect() // also clears the interval
      return
    }

    msRemaining.value = remaining
  }, 100)
})

onMounted(async () => {
  loadRedirectConfig()
      .then(() => {
        checked.value = Boolean(localStorage.getItem(localStorageKey))
        if (checked.value && URL.value) {
          startCountdown()
        }
      })
})

onUnmounted(() => {
  if (intervalId !== undefined) {
    clearInterval(intervalId)
  }
})
</script>

<template>
  <jha-card v-if="configLoaded">
    <jha-flex-wrapper>
      <img src="./img/logo.png"/>
    </jha-flex-wrapper>

    <jha-flex-wrapper>
      <div style="padding: 0 15px 0 0">
        <jha-progress v-if="isCounting"
                      card/>
      </div>
      <div>
        This domain has been migrated, please visit the new domain at:<br/>
        <a :href="URL ?? undefined">{{ URL }}</a>
        <br/>
        <small v-if="isCounting">Redirecting in {{ Math.ceil(msRemaining / 1000) }}s...</small>
      </div>
    </jha-flex-wrapper>


    <jha-flex-wrapper space-between style="padding: 0">
      <jha-form-checkbox :checked
                         @change="toggleCheckbox">Do not remind me next time
      </jha-form-checkbox>
      <jha-button @click="redirect()">
        Redirect
      </jha-button>
    </jha-flex-wrapper>
  </jha-card>
</template>

<style scoped>
jha-flex-wrapper {
  padding-bottom: 25px
}

jha-card {
  position: fixed;
  max-width: 600px;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  margin: auto;
  padding: 15px;
  height: fit-content;
}

img {
  max-width: 65%;
  height: auto;
  left: 0;
  right: 0;
  margin: auto;
}
</style>
